/** Add an angle — resumable Start (facets 1c-3, Decisions 5–6).
 *  create the angle → add the checked seeds tagged with it → trawl them with the angle's effective
 *  recency → request scoring for every unscored paper (no ids: the backend picks them). The facet id and
 *  the last completed phase live in refs, so a retry after a later failure resumes there and never makes
 *  a second angle. Mirrors components/setup/useStartSetup.ts (seed refs, ensureNetworks retry). */
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { AddAngleMode } from "@/components/angles/AddAngleSheet";
import { toastFacetError } from "@/components/angles/toastFacetError";
import { httpError } from "@/components/setup/http";
import { type FacetCreateVars, useCreateFacet } from "@/hooks/useFacets";
import { useAddPapers } from "@/hooks/usePapers";
import { useScoring } from "@/hooks/useScoring";
import { effectiveScope } from "@/lib/angles";
import apiClient from "@/lib/api-client";
import { postRunTiming, type RunOutcome } from "@/lib/runTiming";
import { candidateToPaperCreate } from "@/lib/seedPayload";
import { ensureNetworks } from "@/lib/setup/ensureNetworks";
import { toast } from "@/lib/toast";
import { networkToPapers, postBatches } from "@/lib/trawl-utils";
import type { SeedCandidate } from "@/stores/seed-discovery-store";
import { seedKey } from "@/stores/setup-store";
import type { Concept, ConceptScope, FacetSummary, ProjectQuestion } from "@/types/concept";
import type { PaperListResponse } from "@/types/paper";

export type AddAngleState = "idle" | "creating" | "seeding" | "trawling" | "scoring" | "done" | "error";
type Phase = "creating" | "seeding" | "trawling" | "scoring";
const PHASES: Phase[] = ["creating", "seeding", "trawling", "scoring"];
export const RUNNING_STATES: AddAngleState[] = PHASES;

export interface AddAngleArgs {
  /** `new` mode only. */
  draft?: Omit<FacetCreateVars, "scope" | "projectQuestion">;
  /** `new` mode only: the angle's own scope overrides. */
  scope?: ConceptScope;
  checked: SeedCandidate[];
  projectQuestion?: ProjectQuestion;
}

export const NO_NETWORK_COPY = "Couldn't load these seeds' citation networks — use Trawl this angle later";

const statusOf = (err: unknown): number | undefined => {
  const status = (err as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === "number" ? status : undefined;
};
/** A 4xx is a definite "no": nothing was saved. A 5xx, timeout or network error may have committed. */
const isClientError = (err: unknown) => {
  const status = statusOf(err);
  return status !== undefined && status >= 400 && status < 500;
};

export const SCORING_BUSY_COPY =
  "Scoring couldn't start — another scoring run is in progress. Retry when it finishes.";

/** A step the server definitely refused: a create answered with a 4xx (nothing was created), or a
 *  scoring request it didn't accept. `reason` is shown in the sheet. */
export interface AddAngleFailure {
  reason: string;
  /** Plan-cap 403: Retry can't help, Upgrade can. */
  upgrade: boolean;
}

async function describeFailure(err: unknown): Promise<AddAngleFailure> {
  const { status, detail } = await httpError(err);
  const headers = (err as { response?: Response }).response?.headers;
  const upgrade = status === 403
    && (headers?.get("X-Error-Code") === "upgrade_required" || !!detail?.includes("Upgrade for more angles"));
  return { reason: detail || "Couldn't add that angle.", upgrade };
}

export function useAddAngle(concept: Concept, mode: AddAngleMode) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createFacet = useCreateFacet(concept.id);
  const addPapers = useAddPapers(concept.id);
  const { requestScoring } = useScoring(concept.id);
  const [state, setState] = useState<AddAngleState>("idle");
  const seedsMode = mode.kind === "seeds";
  const [facetId, setFacetId] = useState<string | null>(seedsMode ? mode.facet.id : null);
  const [failure, setFailure] = useState<AddAngleFailure | null>(null);
  const facetIdRef = useRef<string | null>(seedsMode ? mode.facet.id : null);
  /** Index into PHASES of the next phase to run (seeds mode never creates). */
  const nextPhase = useRef(seedsMode ? 1 : 0);
  const busyRef = useRef(false);
  /** Facet ids that existed before the FIRST create attempt: a retry must not count an angle an
   *  earlier, ambiguous attempt made (and the refetch may since have put in the concept) as "old". */
  const beforeIds = useRef<Set<string> | null>(null);
  /** An earlier create failed without an HTTP answer: look for its angle before creating again. */
  const ambiguous = useRef(false);
  /** One per Start; aborted on unmount so the trawl's batch posts stop. */
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);

  const conceptId = concept.id;

  async function create(args: AddAngleArgs): Promise<string> {
    const draft = args.draft!;
    const label = draft.label.trim();
    beforeIds.current ??= new Set((concept.facet_list ?? []).map((f) => f.id));
    const before = beforeIds.current;
    // Match by label only: the server sanitizes the question (e.g. "&"), so it may not round-trip.
    const isNew = (f: FacetSummary) => !before.has(f.id) && !f.id.startsWith("pending-");
    const adopt = async () => {
      const fresh = await apiClient.get(`api/concepts/${conceptId}`).json<Concept>();
      queryClient.setQueryData(["concepts", conceptId], fresh);
      return (fresh.facet_list ?? []).find((f) => isNew(f) && f.label === label)?.id ?? null;
    };
    if (ambiguous.current) {
      const prior = await adopt();
      if (prior) return prior;
    }
    const vars: FacetCreateVars = {
      ...draft, label, question: draft.question.trim(), search_terms: draft.search_terms.trim(),
      scope: args.scope ?? {}, projectQuestion: args.projectQuestion,
    };
    try {
      const res = await createFacet.mutateAsync(vars);
      const list = res.facet_list ?? [];
      const id = list.find((f) => isNew(f) && f.label === label)?.id ?? list.find(isNew)?.id;
      if (!id) throw new Error("created angle not found");
      ambiguous.current = false;
      return id;
    } catch (err) {
      if (isClientError(err)) {
        // A definite refusal (cap 403, validation 422…): nothing was created.
        ambiguous.current = false;
        setFailure(await describeFailure(err));
        void toastFacetError(err, "Couldn't add that angle.", () => navigate({ to: "/settings/billing" }));
        throw err;
      }
      // 5xx / timeout / network: the create may have committed. Adopt it rather than make a second
      // angle; if it isn't there, a Retry reconciles again before re-POSTing.
      ambiguous.current = true;
      const adopted = await adopt();
      if (!adopted) throw err;
      ambiguous.current = false;
      return adopted;
    }
  }

  async function trawl(id: string, recency: ConceptScope["recency"], signal: AbortSignal): Promise<boolean> {
    const { papers: all } = await apiClient
      .get(`api/concepts/${conceptId}/papers`, { searchParams: { role: "foundational", per_page: "500" } })
      .json<PaperListResponse>();
    const rows = all.filter((r) => r.seed_facet_ids?.includes(id));
    const seeds = rows.map((r) => ({ key: seedKey(r), paper_id: r.paper_id, doi: r.doi, title: r.title }));
    // Reuse what the ③ preview settled; ask S2 again only if every seed came back empty.
    let ensured = await ensureNetworks(seeds, recency, { retryFailed: false });
    if (!ensured.some((e) => e.network)) ensured = await ensureNetworks(seeds, recency, { retryFailed: true });
    const items = ensured.flatMap(({ network }, i) =>
      network ? [{ network, parent: { paperId: rows[i].id, method: "trawl" as const } }] : [],
    );
    if (!items.length) return false;
    const papers = networkToPapers(items);
    if (papers.length) await postBatches(conceptId, papers, signal);
    if (signal.aborted) throw new Error("aborted");
    return true;
  }

  async function score() {
    // The refetch isn't awaited (it's for the lists); acceptance is — a refused start (409: another
    // run is going) must end in "error" so Retry resumes here. The stream itself runs on into the
    // global scoring store Curate renders. No paper_ids: the backend scores every paper unscored for
    // any active angle — a client-side list (capped page, pre-refetch cache) could miss rows. No
    // `interactive`: that's honoured for setup only (Decision 5).
    void queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    void queryClient.invalidateQueries({ queryKey: ["paper-keys", conceptId] });
    try {
      await requestScoring({});
    } catch (err) {
      const { status, detail } = await httpError(err);
      setFailure({
        reason: status === 409 ? SCORING_BUSY_COPY
          : `Scoring couldn't start${detail ? ` — ${detail.replace(/\.$/, "")}` : ""}. Retry in a moment.`,
        upgrade: false,
      });
      throw err;
    }
  }

  async function start(args: AddAngleArgs): Promise<string | null> {
    if (busyRef.current) return null;
    busyRef.current = true;
    const ctrl = new AbortController();
    controller.current = ctrl;
    setFailure(null);
    // Time whole runs only: a resume after an error starts mid-way.
    const timed = nextPhase.current === 0;
    const startedAt = Date.now();
    let outcome: RunOutcome = "cancelled";
    try {
      const recency = seedsMode
        ? effectiveScope(mode.facet, concept.scope).recency
        : effectiveScope({ scope: args.scope ?? null }, concept.scope).recency ?? concept.scope?.recency;
      for (let i = nextPhase.current; i < PHASES.length; i++) {
        if (ctrl.signal.aborted) throw new Error("aborted");
        const phase = PHASES[i];
        setState(phase);
        if (phase === "creating") {
          const id = await create(args);
          facetIdRef.current = id;
          setFacetId(id);
        } else if (phase === "seeding") {
          await addPapers.mutateAsync({
            papers: args.checked.map(candidateToPaperCreate), role: "foundational",
            added_source: "seed_discovery", seed_facet_ids: [facetIdRef.current!],
          });
        } else if (phase === "trawling") {
          if (!(await trawl(facetIdRef.current!, recency, ctrl.signal))) toast.warning(NO_NETWORK_COPY);
        } else {
          await score();
        }
        nextPhase.current = i + 1;
      }
      setState("done");
      outcome = "success";
      return facetIdRef.current;
    } catch {
      if (!ctrl.signal.aborted) outcome = "failed";
      setState("error");
      return null;
    } finally {
      busyRef.current = false;
      if (timed) postRunTiming(concept.id, "add_angle_setup", args.checked.length, startedAt, outcome);
    }
  }

  return { start, state, facetId, failure };
}
